// src/Pages/ParentEventCalendar.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Container,
  Fade,
  alpha,
  FormControl,
  Select,
  MenuItem,
  Tooltip
} from "@mui/material";
import { EventAvailable } from "@mui/icons-material";
import apiEndpoints from "../apiconfig";

const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ParentEventCalendar = () => {
  const token = localStorage.getItem("parent_token");

  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [current, setCurrent] = useState(null);

  const year = current?.year;
  const monthIndex = current?.monthIndex;

  /* ---------- Calendar Grid ---------- */
  const { cells } = useMemo(() => {
    if (year == null || monthIndex == null) return { cells: [] };

    const firstDay = new Date(year, monthIndex, 1).getDay();
    const offset = (firstDay + 6) % 7;
    const days = new Date(year, monthIndex + 1, 0).getDate();

    return {
      cells: Array.from({ length: offset + days }, (_, i) =>
        i < offset ? null : i - offset + 1
      ),
    };
  }, [year, monthIndex]);

  /* ---------- Months ---------- */
  useEffect(() => {
    fetch(`${apiEndpoints.calendar}?action=months`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((res) => {
       if (res.status) {
         const monthsData = res.data.months;
         setMonths(monthsData);

         const now = new Date();
         const currentYear = now.getFullYear();
         const currentMonth = now.getMonth() + 1;

         const currentMonthObj = monthsData.find(
           (m) => m.year === currentYear && m.month === currentMonth
         );

         // ✅ Select current month if exists, else fallback to first
         setSelectedMonth(currentMonthObj || monthsData[0]);
       }
      });
  }, []);

  /* ---------- Events ---------- */
  useEffect(() => {
    if (!selectedMonth) return;

    fetch(
      `${apiEndpoints.calendar}?action=month&year=${selectedMonth.year}&month=${selectedMonth.month}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then((res) => res.json())
      .then((res) => {
        setCurrent({
          year: selectedMonth.year,
          monthIndex: selectedMonth.month - 1,
          days: res.status ? res.data.events || {} : {},
        });
      });
  }, [selectedMonth]);

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "rgba(255, 255, 255, 0.25)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
             radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
           `,
          pointerEvents: "none",
        },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ position: "relative", zIndex: 1, px: { xs: 1.5, sm: 2, md: 3 } }}
      >
        {/* HEADER */}
        <Fade in timeout={700}>
          <Box
            sx={{
              py: 4,
              px: { xs: 2, md: 4 },
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: { xs: 2, sm: 3 },
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 0.5,
                  fontSize: { xs: "1.7rem", md: "2.1rem" },
                }}
              >
                School Event Calendar
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                Upcoming events for your school
              </Typography>
            </Box>

            {/* Month dropdown ONLY */}
            <FormControl sx={{ minWidth: { xs: "100%", sm: 180 } }}>
              <Select
                value={selectedMonth?.label || ""}
                onChange={(e) => {
                  const m = months.find((x) => x.label === e.target.value);
                  setSelectedMonth(m);
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: {
                    sx: {
                      borderRadius: "14px",
                      maxHeight: 320,
                      zIndex: 1500,
                    },
                  },
                }}
                sx={{
                  bgcolor: "rgba(255,255,255,0.95)",
                  borderRadius: "14px",
                  px: 1.5,
                }}
              >
                {months.map((m) => (
                  <MenuItem key={m.label} value={m.label}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Fade>

        {/* CALENDAR */}
        <Fade in timeout={1000}>
          <Box sx={{ px: { xs: 2, md: 4 }, pb: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "22px",
                background: "rgba(255,255,255,0.7)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
              }}
            >
              {/* Calendar Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2.5,
                }}
              >
                <EventAvailable />
                <Typography fontWeight={600}>Calendar View</Typography>
              </Box>

              {/* Weekdays */}
              <Grid container columns={{ xs: 3, sm: 4, md: 7 }} sx={{ mb: 1 }}>
                {weekdayLabels.map((w) => (
                  <Grid item xs={1} key={w}>
                    <Box
                      sx={{
                        textAlign: "center",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#9e9e9e",
                      }}
                    >
                      {w}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Days */}
              <Grid container columns={{ xs: 3, sm: 4, md: 7 }} spacing={1.2}>
                {cells.map((date, idx) => (
                  <Grid item xs={1} key={idx}>
                    <Box
                      sx={{
                        borderRadius: "14px",
                        height: { xs: 80, sm: 100, md: 120 },
                        p: { xs: 0.6, sm: 1 },
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "rgba(255,255,255,0.9)",
                        border: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: 13, sm: 16 },
                          color: "#616161",
                        }}
                      >
                        {date}
                      </Typography>

                      <Box
                        sx={{
                          mt: 0.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          overflowY: "auto",
                          flexGrow: 1,
                          pr: 0.5,
                        }}
                      >
                        {(current.days[date] || []).map((ev) => (
                          <Tooltip
                            key={ev.id}
                            title={ev.title}
                            arrow
                            placement="top"
                          >
                            <Chip
                              label={ev.title}
                              size="small"
                              sx={{
                                bgcolor: alpha("#1976D2", 0.12),
                                color: "#1565C0",
                                fontWeight: 600,
                                maxWidth: "100%",
                                cursor: "pointer",

                                // 🔥 ELLIPSIS MAGIC
                                "& .MuiChip-label": {
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                },
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ParentEventCalendar;
